<!-- BETA-CLEANUP: delete this file and its spec once Vue 3 is the only app -->
<template>
  <v-dialog v-model="isOpen" max-width="440">
    <v-card data-test="beta-intro-dialog" class="beta-intro-dialog">
      <div class="beta-intro-dialog__hero primary--text text-center px-6 pt-8 pb-6">
        <v-btn
          icon
          small
          absolute
          top
          right
          aria-label="Close"
          class="beta-intro-dialog__close"
          @click="snooze"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-avatar color="primary" size="56" class="mb-4">
          <BetaRocketIcon :size="32" class="white--text" />
        </v-avatar>
        <div class="d-flex align-center justify-center">
          <span class="text-h6 text--primary">Try the new SlickCactus</span>
          <CommonChip color="primary" class="ml-2">New</CommonChip>
        </div>
        <p class="body-2 text--secondary mt-2 mb-0">
          A fresh design for the app you use every day.
        </p>
        <p v-if="releaseNote" class="body-2 font-weight-medium mt-2 mb-0" data-test="beta-intro-release">
          {{ releaseNote }}
        </p>
      </div>
      <v-list dense class="px-4 py-3">
        <v-list-item v-for="point in points" :key="point.text">
          <v-list-item-icon class="mr-4">
            <v-icon color="primary">{{ point.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="body-2">{{ point.text }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <div class="px-6 pb-6">
        <v-btn
          color="primary"
          depressed
          block
          large
          :loading="isOpeningBeta"
          data-test="beta-intro-try"
          data-track="beta.intro-dialog.open"
          @click="openBeta"
        >
          Try the new version
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn
          text
          block
          class="mt-2"
          data-test="beta-intro-later"
          @click="snooze"
        >
          Not now
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';

import { getBetaReleaseNote, openBeta, snoozeBetaIntro } from '@/bundles/App/helpers/betaChannel';

import BetaRocketIcon from '@/bundles/App/components/BetaRocketIcon.vue';
import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';

interface IBetaIntroPoint {
  icon: string;
  text: string;
}

interface IData {
  isOpen: boolean;
  releaseNote: string;
  isOpeningBeta: boolean;
  points: ReadonlyArray<IBetaIntroPoint>;
}

export default Vue.extend({
  name: 'BetaIntroDialog',

  components: { BetaRocketIcon, CommonChip },

  data: (): IData => ({
    isOpen: true,
    releaseNote: getBetaReleaseNote(),
    isOpeningBeta: false,
    points: Object.freeze([
      { icon: 'mdi-database-check-outline', text: 'Same data, nothing to set up' },
      { icon: 'mdi-account-check-outline', text: 'You stay signed in on the same page' },
      { icon: 'mdi-swap-horizontal', text: 'Opens for you from now on; switch back any time' },
    ]),
  }),

  watch: {
    // closing by Esc or a click outside counts as "Not now"
    isOpen (value: boolean): void {
      if (!value) snoozeBetaIntro();
    },
  },

  methods: {
    snooze (): void {
      this.isOpen = false;
    },
    async openBeta (): Promise<void> {
      this.isOpeningBeta = true;

      try {
        await openBeta();
      } finally {
        this.isOpeningBeta = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
// a light tint of the primary colour behind the header, readable in both themes
.beta-intro-dialog__hero {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: currentColor;
    opacity: 0.08;
    pointer-events: none;
  }
}

.beta-intro-dialog__close {
  z-index: 1;
}
</style>
